import { useState } from 'react';
import { useGmbMonthlyAudit, useGmbRecommendations } from '../../hooks/useGmbOptimization';
import { GBMContentWrapper } from '../governance/shared/GMBLoadingState';
import GMBEmptyState from '../governance/shared/GMBEmptyState';
import type { GmbMonthlyAuditResponse, GmbRecommendationsResponse } from '../../types';

interface Props {
  darkMode?: boolean;
  onNavigate?: (section: string) => void;
}

function SectionTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-6">
      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{title}</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>
    </div>
  );
}

export default function MonthlyOptimizationActionPlan({ darkMode, onNavigate }: Props) {
  const recs = useGmbRecommendations();
  const audit = useGmbMonthlyAudit();
  const isLoading = recs.isLoading || audit.isLoading;
  const error = recs.error || audit.error;
  const refetch = () => {
    recs.refetch();
    audit.refetch();
  };

  return (
    <GBMContentWrapper error={error} onRetry={refetch} loading={isLoading}>
      <Inner
        darkMode={darkMode}
        onNavigate={onNavigate}
        recommendations={(recs.data || {}) as GmbRecommendationsResponse}
        audit={(audit.data || {}) as GmbMonthlyAuditResponse}
      />
    </GBMContentWrapper>
  );
}

function Inner({ recommendations, audit, onNavigate }: Props & { recommendations: GmbRecommendationsResponse; audit: GmbMonthlyAuditResponse }) {
  const [activeTab, setActiveTab] = useState<'improvements' | 'declines' | 'opportunities'>('improvements');
  const pending = recommendations.recommendations || [];
  const resolved = recommendations.recently_resolved || [];
  const scoreChange = audit.has_previous ? (audit.current_health_score || 0) - (audit.previous_health_score || 0) : 0;

  if (!pending.length && !audit.health_score_trend?.length && !audit.improvements?.length && !audit.has_previous && !(audit.current_health_score > 0)) {
    return <GMBEmptyState title="No monthly audit yet" message="Run optimization analysis to populate recommendations and monthly audit tracking." icon="ri-calendar-check-line" />;
  }

  return (
    <div className="space-y-8">
      <section>
        <SectionTitle
          title="Action Recommendations"
          subtitle="Based on performance trends and profile activity, actionable recommendations to keep your GMB optimized"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {pending.map((rec, i) => (
            <RecommendationCard
              key={`${rec.title}-${i}`}
              type={rec.severity}
              message={rec.title}
              impact={rec.description}
              action={rec.cta_label}
              ctaAction={rec.cta_action}
              icon={iconForAction(rec.cta_action)}
              onNavigate={onNavigate}
            />
          ))}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Recently Resolved</h3>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {resolved.length === 0 ? (
              <div className="px-5 py-4 text-xs text-gray-500">No recently resolved recommendations.</div>
            ) : resolved.map((rec) => (
              <div key={`${rec.title}-${rec.resolved_date}`} className="flex items-center gap-3 px-5 py-3">
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-double-line text-green-600"></i>
                </div>
                <p className="flex-1 text-sm text-gray-900 dark:text-gray-100">{rec.title}</p>
                <span className="text-xs text-gray-400 flex-shrink-0">{rec.resolved_date}</span>
                <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full flex-shrink-0">Resolved</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section>
        <SectionTitle
          title="Monthly Audit & Improvement Tracking"
          subtitle="Automatic profile analysis comparing current performance against previous audits"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <ScoreCard title="Current Health Score" value={`${audit.current_health_score || 0}%`} badge={`${scoreChange >= 0 ? '+' : ''}${scoreChange}`} subtitle={`Audited on ${audit.current_audit_date || '—'}`} positive={scoreChange >= 0} />
          <ScoreCard title="Previous Score" value={audit.has_previous ? `${audit.previous_health_score || 0}%` : '—'} subtitle={audit.has_previous ? `Audited on ${audit.previous_audit_date || '—'}` : 'First audit — no previous data'} />
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Improvements vs Declines</p>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <Pill icon="ri-arrow-up-line" color="text-green-600" value={audit.improved_count || 0} label="improved" />
              <Pill icon="ri-arrow-down-line" color="text-red-500" value={audit.declined_count || 0} label="declined" />
              <Pill icon="ri-lightbulb-line" color="text-orange-500" value={audit.pending_count || 0} label="pending" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Profile Health Score Trend</h3>
          {(audit.health_score_trend || []).length >= 3 ? (
            <AuditScoreChart history={audit.health_score_trend || []} />
          ) : (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {(audit.health_score_trend || []).length === 0
                  ? 'No trend data yet — your score history will build as you sync your profile over time.'
                  : `Trend chart will appear after 3+ syncs. Currently showing ${(audit.health_score_trend || []).length} data point(s).`
                }
              </p>
              {(audit.health_score_trend || []).length > 0 && (
                <div className="flex items-center justify-center gap-4 mt-4">
                  {(audit.health_score_trend || []).map((d) => (
                    <div key={d.month} className="text-center">
                      <div className="text-2xl font-bold text-teal-600">{d.score}%</div>
                      <div className="text-xs text-gray-500 mt-1">{d.month}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="flex border-b border-gray-100 dark:border-gray-700">
            {[
              { key: 'improvements', label: 'Improvements', count: audit.improvements?.length || 0, color: 'text-green-600' },
              { key: 'declines', label: 'Declines', count: audit.declines?.length || 0, color: 'text-red-600' },
              { key: 'opportunities', label: 'Pending Opportunities', count: audit.pending_opportunities?.length || 0, color: 'text-orange-600' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.key ? 'border-teal-600 text-teal-700 bg-teal-50/50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                }`}
              >
                <span className="flex items-center justify-center gap-1.5">
                  {tab.label}
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 ${tab.color}`}>{tab.count}</span>
                </span>
              </button>
            ))}
          </div>
          <div className="p-5">
            {activeTab === 'improvements' && <AuditTable rows={audit.improvements || []} emptyText={audit.has_previous ? undefined : 'Changes will appear after your next profile sync'} />}
            {activeTab === 'declines' && <AuditTable rows={audit.declines || []} negative emptyText={audit.has_previous ? undefined : 'Changes will appear after your next profile sync'} />}
            {activeTab === 'opportunities' && <AuditTable rows={audit.pending_opportunities || []} opportunity emptyText={audit.has_previous ? undefined : 'Changes will appear after your next profile sync'} />}
          </div>
        </div>

        <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">KPI Movement — Current vs Previous Audit Period</h3>
          <p className="text-[10px] text-gray-400 mb-4">Location-level metrics from Google Business Profile</p>
          {(audit.kpi_movement || []).length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(audit.kpi_movement || []).map((kpi) => (
                <div key={kpi.metric} className="rounded-lg border border-gray-100 dark:border-gray-700 p-4">
                  <p className="text-xs text-gray-500 mb-1">{kpi.metric.replace(/_/g, ' ')}</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{kpi.current.toLocaleString()}</span>
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${(kpi.change_pct || 0) >= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                      {(kpi.change_pct || 0) >= 0 ? '+' : ''}{kpi.change_pct ?? 0}%
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">was {kpi.previous.toLocaleString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500 text-center py-4">KPI comparison will appear after your next profile sync.</p>
          )}
        </div>
      </section>
    </div>
  );
}

function RecommendationCard({ type, message, impact, action, ctaAction, icon, onNavigate }: { type: string; message: string; impact: string; action: string; ctaAction: string; icon: string; onNavigate?: (section: string) => void }) {
  const borderColors: Record<string, string> = {
    critical: 'border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800',
    warning: 'border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800',
    informational: 'border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800',
  };
  const badgeColors: Record<string, string> = {
    critical: 'bg-red-100 text-red-700',
    warning: 'bg-orange-100 text-orange-700',
    informational: 'bg-blue-100 text-blue-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${borderColors[type] || 'border-gray-200 bg-white dark:bg-gray-800'}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${badgeColors[type] || 'bg-gray-100'}`}>
          <i className={`${icon} text-lg`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${badgeColors[type] || 'bg-gray-100 text-gray-600'}`}>{type}</span>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-2 mb-1">{message}</p>
          <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">{impact}</p>
          <button onClick={() => onNavigate?.(ctaAction)} className="text-xs font-medium text-teal-700 bg-teal-50 px-3 py-1.5 rounded-lg hover:bg-teal-100 transition-colors">{action || 'View Details'}</button>
        </div>
      </div>
    </div>
  );
}

function AuditScoreChart({ history }: { history: { month: string; score: number }[] }) {
  const width = 560;
  const height = 160;
  const padding = { top: 20, right: 20, bottom: 30, left: 40 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const getX = (i: number) => padding.left + (history.length <= 1 ? 0 : (i / (history.length - 1)) * chartW);
  const getY = (score: number) => padding.top + chartH - ((score - 50) / 50) * chartH;
  const path = history.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(d.score)}`).join(' ');

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="w-full">
      {[60, 70, 80, 90].map((val) => (
        <line key={val} x1={padding.left} y1={getY(val)} x2={width - padding.right} y2={getY(val)} stroke="#e5e7eb" strokeDasharray="4 4" />
      ))}
      <path d={path} fill="none" stroke="#0d9488" strokeWidth={2.5} strokeLinejoin="round" />
      {history.map((d, i) => (
        <g key={d.month}>
          <circle cx={getX(i)} cy={getY(d.score)} r={4} fill="#0d9488" stroke="white" strokeWidth={2} />
          <text x={getX(i)} y={getY(d.score) - 10} textAnchor="middle" fontSize="11" fill="#374151" fontWeight="600">{d.score}</text>
          <text x={getX(i)} y={height - 6} textAnchor="middle" fontSize="11" fill="#6b7280">{d.month}</text>
        </g>
      ))}
    </svg>
  );
}

function ScoreCard({ title, value, badge, subtitle, positive = true }: { title: string; value: string; badge?: string; subtitle: string; positive?: boolean }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{title}</p>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">{value}</span>
        {badge && <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${positive ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>{badge}</span>}
      </div>
      <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
    </div>
  );
}

function Pill({ icon, color, value, label }: { icon: string; color: string; value: number; label: string }) {
  return <div className="flex items-center gap-1"><i className={`${icon} ${color}`} /><span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{value}</span><span className="text-xs text-gray-500">{label}</span></div>;
}

function AuditTable({ rows, negative, opportunity, emptyText }: { rows: Array<{ category: string; current: string; previous: string; change: string }>; negative?: boolean; opportunity?: boolean; emptyText?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 dark:border-gray-700">
            <th className="text-left py-2 pr-4 text-xs font-medium text-gray-500 uppercase">Category</th>
            <th className="text-left py-2 pr-4 text-xs font-medium text-gray-500 uppercase">{opportunity ? 'Current' : 'Current'}</th>
            <th className="text-left py-2 pr-4 text-xs font-medium text-gray-500 uppercase">Previous</th>
            <th className="text-left py-2 text-xs font-medium text-gray-500 uppercase">Change</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
          {rows.length === 0 ? (
            <tr><td colSpan={4} className="py-4 text-xs text-gray-500">{emptyText || 'No rows available'}</td></tr>
          ) : rows.map((item) => (
            <tr key={`${item.category}-${item.change}`}>
              <td className="py-3 pr-4 font-medium text-gray-900 dark:text-gray-100">{item.category}</td>
              <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">{item.current}</td>
              <td className="py-3 pr-4 text-gray-500">{item.previous}</td>
              <td className="py-3">
                <span className={`inline-flex items-center gap-1 font-medium text-xs px-2 py-0.5 rounded-full ${negative ? 'text-red-600 bg-red-50' : opportunity ? 'text-orange-600 bg-orange-50' : 'text-green-600 bg-green-50'}`}>
                  <i className={negative ? 'ri-arrow-down-line' : opportunity ? 'ri-lightbulb-line' : 'ri-arrow-up-line'}></i>
                  {item.change}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function iconForAction(action: string) {
  if (action?.includes('photo')) return 'ri-image-add-line';
  if (action?.includes('review')) return 'ri-star-line';
  if (action?.includes('post')) return 'ri-calendar-close-line';
  if (action?.includes('profile')) return 'ri-file-edit-line';
  return 'ri-lightbulb-line';
}
